import type { Condition } from "@skilitics/judy";
import { isTernaryOperator } from "@skilitics/judy";
import type { PrimitiveValue } from "@skilitics/nautilus-core";
import type { ValueExpression } from "@skilitics/nautilus-core-designer-data";

import type { ConditionValueResolvable } from "../resolveValueConfigs.ts";
import type { ValueExpressionContext } from "./valueExpressionValidity.ts";
import { isValueExpressionValid } from "./valueExpressionValidity.ts";

export interface BuildRuntimeExpressionContext extends ValueExpressionContext {
  /**
   * The subject, already resolved by the caller. Callers reach it by different
   * routes — a node-owned buildable names a property of its own result, a
   * free-standing one resolves the picked reference — and the assembly is the
   * same either way.
   */
  subject: PrimitiveValue | ConditionValueResolvable;
}

/**
 * Assemble the judy Condition an expression means. This is the expression's only
 * runtime manifestation: nothing subtype-aware crosses over, and the base type
 * on the condition is all the player is told.
 *
 * Abstains — returns undefined — for anything invalid, rather than emitting a
 * partial condition. A trigger with an unbuildable condition contributes
 * nothing, which is what every condition builder already does when an operand
 * fails to resolve.
 *
 * `value`/`value2` are arity-stable in storage; judy's `object` and `left`/
 * `right` appear only here, chosen by the operator's arity.
 */
export const buildRuntimeExpression = (
  expression: ValueExpression,
  context: BuildRuntimeExpressionContext,
): Condition | undefined => {
  if (!isValueExpressionValid(expression, context)) return undefined;

  const { signature, definition, resolveValue, subject } = context;
  const { operator, value, value2 } = expression;
  const requirement = signature?.subtype && definition.require?.(signature.subtype);
  const condition = { type: definition.type, subject, operator };

  if (operator && isTernaryOperator(operator)) {
    const left = resolveValue(value, definition.type, requirement);
    const right = resolveValue(value2, definition.type, requirement);
    if (left === undefined || right === undefined) return undefined;
    return { ...condition, left, right } as Condition;
  }

  const object = resolveValue(value, definition.type, requirement);
  if (object === undefined) return undefined;
  return { ...condition, object } as Condition;
};
